import {FC} from 'react'
import {FilterOptions, hasActiveFilters, TASK_STATUSES, TaskFilters} from '../taskFilters'

type Props = {
  filters: TaskFilters
  options: FilterOptions
  onChange: (patch: Partial<TaskFilters>) => void
  onClear: () => void
  search?: string
  onSearchChange?: (value: string) => void
  count?: number
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  cancelled: 'Cancelled',
}

const STATUS_OPTIONS: {value: string; label: string}[] = [
  {value: '', label: 'All'},
  ...TASK_STATUSES.map((s) => ({value: s, label: STATUS_LABELS[s] ?? s})),
]

// Shared filter bar for BOTH task views (list + calendar). Visual language matches the
// Support Tickets tab on the member profile: debounced-free search input, segmented status
// chips, and admin-only dropdowns (staff / entity / activity) alongside. The dropdown data
// is derived from the loaded tasks (empty until the enriched serializer lands — the selects
// simply show no options and don't filter).
export const TaskFilterBar: FC<Props> = ({
  filters,
  options,
  onChange,
  onClear,
  search = '',
  onSearchChange,
  count,
}) => {
  return (
    <div className='support-tickets-filters nb-task-filter-bar'>
      {onSearchChange && (
        <div className='support-tickets-filters__search'>
          <input
            type='text'
            placeholder='Search by title or note…'
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      )}

      <div
        className='support-tickets-filters__chips'
        role='tablist'
        aria-label='Filter by status'
      >
        {STATUS_OPTIONS.map((opt) => (
          <button
            type='button'
            key={opt.value || 'all'}
            role='tab'
            aria-selected={filters.status === opt.value}
            className={`support-tickets-filters__chip${filters.status === opt.value ? ' support-tickets-filters__chip--active' : ''}`}
            onClick={() => onChange({status: opt.value})}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className='nb-task-filter-bar__selects'>
        <select
          className='form-select form-select-sm w-auto'
          aria-label='Filter by staff'
          value={filters.staff}
          onChange={(e) => onChange({staff: e.target.value})}
          disabled={options.staff.length === 0}
        >
          <option value=''>All staff</option>
          {options.staff.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          className='form-select form-select-sm w-auto'
          aria-label='Filter by member or partner'
          value={filters.entity}
          onChange={(e) => onChange({entity: e.target.value})}
          disabled={options.entities.length === 0}
        >
          <option value=''>All members & partners</option>
          {options.entities.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        <select
          className='form-select form-select-sm w-auto'
          aria-label='Filter by activity'
          value={filters.activity}
          onChange={(e) => onChange({activity: e.target.value})}
          disabled={options.activities.length === 0}
        >
          <option value=''>All activities</option>
          {options.activities.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>

        {hasActiveFilters(filters) && (
          <button type='button' className='nb-task-filter-bar__clear' onClick={onClear}>
            Clear filters
          </button>
        )}
      </div>

      {typeof count === 'number' && count > 0 && (
        <span className='support-tickets-filters__count'>
          {count} task{count === 1 ? '' : 's'}
        </span>
      )}
    </div>
  )
}
